import React, { useState, useEffect } from 'react';
import { borrowingApi } from '../services/api';
import UploadBorrowingModal from './UploadBorrowingModal';
import GenerateBorrowingAnalysisModal from './GenerateBorrowingAnalysisModal';
import '../styles/Panel.css';

function BorrowingManagementPanel({ entity, onComplete }) {
  const [borrowingStatus, setBorrowingStatus] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  useEffect(() => {
    if (entity) {
      fetchBorrowingStatus();
      fetchOutputs();
    }
  }, [entity]);

  const fetchBorrowingStatus = async () => {
    try {
      setLoading(true);
      const data = await borrowingApi.getBorrowingStatus(entity.name);
      setBorrowingStatus(data);
    } catch (err) {
      console.error('Failed to fetch borrowing status:', err);
      setError('Failed to load borrowing status');
    } finally {
      setLoading(false);
    }
  };

  const fetchOutputs = async () => {
    try {
      const data = await borrowingApi.listBorrowingOutputs(entity.name);
      setOutputs(data.outputs || []);
    } catch (err) {
      console.error('Failed to fetch borrowing outputs:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setError(null);
      const blob = await borrowingApi.downloadTemplate(entity.name);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `borrowing_input_template_${entity.fileSlug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleDownloadBorrowingData = async () => {
    try {
      setError(null);
      const blob = await borrowingApi.downloadBorrowingData(entity.name);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `borrowing_input_${entity.fileSlug}_${borrowingStatus.latest_upload.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download borrowing data: ' + err.message);
    }
  };

  const handleDownloadOutput = async (output) => {
    try {
      setError(null);
      const blob = await borrowingApi.downloadBorrowingOutput(
        entity.name,
        output.reporting_date,
        output.timestamp
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `borrowing_analysis_${entity.fileSlug}_${output.reporting_date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download output: ' + err.message);
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    fetchBorrowingStatus();
    if (onComplete) onComplete();
  };

  const handleAnalysisComplete = () => {
    setShowAnalysisModal(false);
    fetchOutputs();
    if (onComplete) onComplete();
  };

  if (loading && !borrowingStatus) {
    return (
      <div className="panel borrowing-panel">
        <h2>Borrowing & Finance Costs Working (Current/Non-current)</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="panel borrowing-panel">
      <h2>Borrowing & Finance Costs Working (Current/Non-current)</h2>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Action Buttons */}
      <div className="button-group">
        <button
          className="btn btn-secondary"
          onClick={handleDownloadTemplate}
        >
          Download Template
        </button>
        <button
          className="btn btn-primary"
          onClick={() => setShowUploadModal(true)}
        >
          Upload Borrowing Data
        </button>
      </div>

      {/* Borrowing Data Section */}
      {borrowingStatus && borrowingStatus.has_data ? (
        <div className="section">
          <h3>Latest Borrowing Data</h3>
          <div className="info-card">
            <div className="info-row">
              <span className="info-label">Uploaded:</span>
              <span className="info-value">
                {new Date(borrowingStatus.latest_upload.timestamp.replace(/_/g, '-')).toLocaleString()}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Number of Borrowings:</span>
              <span className="info-value">
                {borrowingStatus.latest_upload.metadata?.borrowing_count || 0}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Number of Lenders:</span>
              <span className="info-value">
                {borrowingStatus.latest_upload.metadata?.lender_count || 0}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Total Principal:</span>
              <span className="info-value">
                ₹ {(borrowingStatus.latest_upload.metadata?.total_principal || 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </span>
            </div>
            {borrowingStatus.latest_upload.metadata?.repayment_count > 0 && (
              <div className="info-row">
                <span className="info-label">Repayments Tracked:</span>
                <span className="info-value">
                  {borrowingStatus.latest_upload.metadata?.repayment_count}
                </span>
              </div>
            )}
          </div>

          <div className="button-group">
            <button
              className="btn btn-secondary"
              onClick={handleDownloadBorrowingData}
            >
              Download Data
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowAnalysisModal(true)}
            >
              Generate Analysis
            </button>
          </div>
        </div>
      ) : (
        <div className="section">
          <p className="no-data-message">
            No borrowing data uploaded yet. Please upload borrowing data to get started.
          </p>
        </div>
      )}

      {/* Previous Analyses Section */}
      {outputs.length > 0 && (
        <div className="section">
          <h3>Previous Analyses</h3>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reporting Date</th>
                  <th>Total Borrowings</th>
                  <th>Total Value</th>
                  <th>Current Portion</th>
                  <th>Non-Current Portion</th>
                  <th>Generated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {outputs.map((output, index) => (
                  <tr key={index}>
                    <td>{output.reporting_date}</td>
                    <td>{output.summary?.total_borrowings || 0}</td>
                    <td>
                      ₹ {(output.summary?.total_outstanding || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td>
                      ₹ {(output.summary?.total_current || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td>
                      ₹ {(output.summary?.total_non_current || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td>{new Date(output.generation_time).toLocaleString()}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadOutput(output)}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showUploadModal && (
        <UploadBorrowingModal
          entity={entity}
          onClose={() => setShowUploadModal(false)}
          onComplete={handleUploadComplete}
        />
      )}

      {showAnalysisModal && (
        <GenerateBorrowingAnalysisModal
          entity={entity}
          onClose={() => setShowAnalysisModal(false)}
          onComplete={handleAnalysisComplete}
        />
      )}
    </div>
  );
}

export default BorrowingManagementPanel;
